// SystemHealthPage — Foundation 2026-05-19 P6 (ADR-121)
//
// Glance-able operational health surface for the BI platform.
// Pulls from:
//   /api/canary-trends       — DAX yesterday vs today for P0.7 canaries
//   /api/domain-freshness    — per-domain last-successful timestamps
//   /api/recent-failures     — ADF failures last 7 days
//
// OI-17 (2026-07-28): /api/dag-status, /api/tests-status, and /api/schema-drift were
// hardcoded stubs returning fabricated healthy state regardless of actual system state —
// deleted (their cards below with them), not implemented. Fake success is worse than
// absence; an honest sparse page is the intended state, not a defect.
//
// Each card polls independently with useSWR-equivalent (manual setInterval).
// 5min refresh for canary trends; 60s for domain freshness + recent failures.

import { useEffect, useState } from "react";

type FetchState<T> = { status: "loading" | "ok" | "error"; data?: T; error?: string };

function useFetchInterval<T>(url: string, intervalMs: number): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({ status: "loading" });
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await fetch(url, { credentials: "include" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setState({ status: "ok", data });
      } catch (ex) {
        if (!cancelled) setState({ status: "error", error: (ex as Error).message });
      }
    }
    void load();
    const t = setInterval(load, intervalMs);
    return () => { cancelled = true; clearInterval(t); };
  }, [url, intervalMs]);
  return state;
}

interface CanaryTrend { measure: string; today: number; yesterday: number; delta_pct: number; expected: number }
interface DomainFreshness { domain: string; last_successful_at: string; status: string }
interface RecentFailure { pipeline: string; run_id: string; failed_at: string; message: string }

function Card(props: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section style={{ background: "#fff", border: "1px solid #e1e1e1", borderRadius: 8, padding: "1rem 1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
      <header style={{ marginBottom: "0.75rem", borderBottom: "1px solid #f0f0f0", paddingBottom: "0.5rem" }}>
        <h2 style={{ margin: 0, fontSize: "1.05rem" }}>{props.title}</h2>
        {props.subtitle && <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem", color: "#666" }}>{props.subtitle}</p>}
      </header>
      {props.children}
    </section>
  );
}

function CanaryTrendsCard() {
  const s = useFetchInterval<{ canaries: CanaryTrend[] }>("/api/canary-trends", 5 * 60_000);
  return (
    <Card title="Canary trends" subtitle="Yesterday vs today (refresh 5min)">
      {s.status === "loading" && <p>Loading…</p>}
      {s.status === "error" && <p style={{ color: "#c00" }}>Error: {s.error}</p>}
      {s.status === "ok" && (
        <table style={{ width: "100%", fontSize: "0.85rem" }}>
          <thead><tr><th align="left">Measure</th><th align="right">Today</th><th align="right">Yesterday</th><th align="right">Δ%</th></tr></thead>
          <tbody>
            {(s.data?.canaries ?? []).map(c => (
              <tr key={c.measure}>
                <td>{c.measure}</td>
                <td align="right">{c.today.toLocaleString()}</td>
                <td align="right">{c.yesterday.toLocaleString()}</td>
                <td align="right" style={{ color: Math.abs(c.delta_pct) > 0.1 ? "#c00" : "#080" }}>{c.delta_pct.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function DomainFreshnessCard() {
  const s = useFetchInterval<{ domains: DomainFreshness[] }>("/api/domain-freshness", 60_000);
  return (
    <Card title="Domain freshness" subtitle="Per-domain last successful window">
      {s.status === "loading" && <p>Loading…</p>}
      {s.status === "error" && <p style={{ color: "#c00" }}>Error: {s.error}</p>}
      {s.status === "ok" && (
        <ul style={{ fontSize: "0.85rem", margin: 0 }}>
          {(s.data?.domains ?? []).map(d => (
            <li key={d.domain}><strong>{d.domain}</strong>: {d.status} · {new Date(d.last_successful_at).toLocaleString()}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RecentFailuresCard() {
  const s = useFetchInterval<{ failures: RecentFailure[] }>("/api/recent-failures", 60_000);
  return (
    <Card title="Recent failures (7d)" subtitle="ADF pipeline runs with status Failed">
      {s.status === "loading" && <p>Loading…</p>}
      {s.status === "error" && <p style={{ color: "#c00" }}>Error: {s.error}</p>}
      {s.status === "ok" && (
        <ul style={{ fontSize: "0.85rem", margin: 0 }}>
          {(s.data?.failures ?? []).slice(0, 5).map((f, i) => (
            <li key={i}><strong>{f.pipeline}</strong> · {new Date(f.failed_at).toLocaleString()} · <span style={{ color: "#666" }}>{f.message.slice(0, 80)}</span></li>
          ))}
          {(s.data?.failures ?? []).length === 0 && <li style={{ color: "#080" }}>No failures in last 7 days.</li>}
        </ul>
      )}
    </Card>
  );
}

export default function SystemHealth() {
  return (
    <main style={{ padding: "1.5rem", fontFamily: "system-ui, sans-serif", maxWidth: "1600px", margin: "0 auto", background: "#f7f7f8", minHeight: "100vh" }}>
      <header style={{ marginBottom: "1.25rem" }}>
        <h1 style={{ margin: 0 }}>System Health</h1>
        <p style={{ margin: "0.25rem 0 0", color: "#666", fontSize: "0.9rem" }}>
          Operational glance for BI cascade · ADR-121 ·{" "}
          <a href="/" style={{ color: "#0078d4" }}>← back to reports</a>
        </p>
      </header>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: "1rem" }}>
        <CanaryTrendsCard />
        <DomainFreshnessCard />
        <RecentFailuresCard />
      </div>
    </main>
  );
}
